import React, { useState } from 'react';
import './AllPlayersTable.css';
import api from '../../../services/api';
import toast from 'react-hot-toast';

const AllPlayersTable = ({ 
  players, 
  myPlayersData = [], // My-players data with purchase amounts
  totalSkins, 
  totalSawoffs, 
  onTotalsUpdate, 
  title = "All Players", 
  showTotals = true, 
  tournament = null,
  onPlayerRemoved = null,
  showRemoveButton = false
}) => {
  const [removingPlayer, setRemovingPlayer] = useState(null);

  const handleRemovePlayer = async (player) => {
    if (!tournament || !player.golferId) {
      toast.error('Cannot remove player: missing tournament or player information');
      return;
    }

    if (tournament.status === 'LIVE') {
      toast.error('Cannot remove players from a live tournament');
      return;
    }

    // Get the purchase amount from myPlayersData
    const playerData = myPlayersData.find(p => p.golferId === player.golferId);
    const purchaseAmount = playerData?.amount || 'N/A';
    
    console.log('🔍 Debug remove player:', {
      player: player,
      myPlayersData: myPlayersData,
      playerData: playerData,
      purchaseAmount: purchaseAmount
    });

    const confirmMessage = `Are you sure you want to remove ${player.golferName || player.name}? You will receive a full refund of $${purchaseAmount}.`;
    
    if (!window.confirm(confirmMessage)) {
      return;
    }

    setRemovingPlayer(player.golferId);
    
    try {
      const response = await api.delete(`/bets/tournaments/${tournament.id}/remove-player`, {
        data: { golferId: player.golferId }
      });

      if (response.data?.success) {
        toast.success(`Successfully removed ${player.golferName || player.name}. Refund processed.`);
        
        // Call the callback to refresh the data
        if (onPlayerRemoved) {
          onPlayerRemoved(player.golferId);
        }
      } else {
        toast.error(response.data?.message || 'Failed to remove player');
      }
    } catch (error) {
      console.error('Error removing player:', error);
      const errorMessage = error.response?.data?.message || 'Failed to remove player';
      toast.error(errorMessage);
    } finally {
      setRemovingPlayer(null);
    }
  };

  const getTierColor = (tier) => {
    const colors = {
      'TIER_A': 'tier-a',
      'TIER_B': 'tier-b',
      'TIER_C': 'tier-c',
      'TIER_D': 'tier-d',
      // Fallback for single letter tiers
      'A': 'tier-a',
      'B': 'tier-b',
      'C': 'tier-c',
      'D': 'tier-d'
    };
    return colors[tier] || 'tier-b';
  };

  const formatTierText = (tier) => {
    if (!tier) return 'Tier D';
    if (tier.startsWith('TIER_')) {
      return tier.replace('TIER_', 'Tier ');
    }
    return `Tier ${tier}`;
  };

  const formatUsername = (fullName) => {
    if (!fullName || fullName === 'N/A') return 'N/A';
    if (fullName === 'System') return 'System';
    
    const nameParts = fullName.trim().split(' ');
    if (nameParts.length === 1) {
      // If only one name, return as is
      return nameParts[0];
    }
    
    // If multiple parts, take first name + last initial
    const firstName = nameParts[0];
    const lastName = nameParts[nameParts.length - 1];
    const lastInitial = lastName.charAt(0).toUpperCase();
    
    return `${firstName}${lastInitial}`;
  };

  const calculatePercentage = (value, total) => {
    if (total === 0) return '0%';
    return `${((value / total) * 100).toFixed(1)}%`;
  };

  // Calculate skin prize amount
  const calculateSkinPrize = (playerSkins, totalSkins) => {
    if (!tournament?.potSize || totalSkins === 0 || playerSkins === 0) return 0;
    
    // Remove saw-off amounts from pot first (each saw-off removes $200 from pot)
    const sawOffDeduction = (totalSawoffs || 0) * 200;
    const availablePotForSkins = Math.max(0, tournament.potSize - sawOffDeduction);
    
    // Calculate player's percentage of skins
    const playerSkinPercentage = playerSkins / totalSkins;
    
    // Calculate prize amount
    return availablePotForSkins * playerSkinPercentage;
  };

  // Calculate saw-off prize amount with pot-limited distribution
  const calculateSawOffPrize = (playerSawOffInfo) => {
    if (!playerSawOffInfo || !Array.isArray(playerSawOffInfo) || playerSawOffInfo.length === 0) {
      return 0;
    }
    
    if (!tournament?.potSize) return 0;
    
    // Sort saw-offs by round and hole (ascending order)
    const sortedSawOffs = [...playerSawOffInfo].sort((a, b) => {
      if (a.round !== b.round) {
        return a.round - b.round; // Sort by round first
      }
      return a.hole - b.hole; // Then by hole
    });
    
    // Calculate how many complete saw-off pairs can be paid
    // Each saw-off pair costs $200 total ($100 per player)
    const maxPayableSawOffPairs = Math.floor(tournament.potSize / 200);
    
    // Only pay for saw-offs that have complete pairs available in pot
    const payableSawOffs = sortedSawOffs.slice(0, maxPayableSawOffPairs);
    
    return payableSawOffs.length * 100;
  };

  // Helper function to get first letter of skin type
  const getSkinTypeLetter = (type) => {
    if (!type) return 'U'; // Unknown
    return type.charAt(0).toUpperCase();
  };

  // Helper function to format skin info
  const formatSkinInfo = (skinInfo) => {
    if (!skinInfo || skinInfo.length === 0) return [];
    const { completedRounds } = calculateRoundCompletion();
    return skinInfo.map(item => ({
      text: `R${item.round}H${item.hole}${getSkinTypeLetter(item.type)}`,
      type: item.type,
      round: item.round,
      isCompleted: item.round <= completedRounds
    }));
  };

  // Helper function to format saw-off info
  const formatSawOffInfo = (sawOffInfo) => {
    if (!sawOffInfo || sawOffInfo.length === 0) return [];
    const { completedRounds } = calculateRoundCompletion();
    return sawOffInfo.map(item => ({
      text: `R${item.round}H${item.hole}${getSkinTypeLetter(item.type)}`,
      type: item.type,
      round: item.round,
      isCompleted: item.round <= completedRounds
    }));
  };

  // Calculate round completion based on tournament dates
  const calculateRoundCompletion = () => {
    if (!tournament || !tournament.startDate) {
      return { completedRounds: 0, currentRound: 1 };
    }

    const today = new Date();
    const tournamentStartDate = new Date(tournament.startDate);
    
    // Reset time to midnight for both dates to compare only dates (ignore time)
    const todayDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const startDate = new Date(tournamentStartDate.getFullYear(), tournamentStartDate.getMonth(), tournamentStartDate.getDate());
    
    const daysSinceStart = Math.floor((todayDate - startDate) / (24 * 60 * 60 * 1000));
    
    // Each round takes one day
    // Day 0: completedRounds = 0, currentRound = 1
    // Day 1: completedRounds = 1, currentRound = 2
    // Day 2: completedRounds = 2, currentRound = 3
    // Day 3: completedRounds = 3, currentRound = 4
    // Day 4+: completedRounds = 4, currentRound = 4 (tournament ended)
    const completedRounds = Math.min(daysSinceStart, 4);
    const currentRound = Math.min(daysSinceStart + 1, 4);

    return { completedRounds, currentRound };
  };

  // Calculate totals and notify parent component (only for main table)
  React.useEffect(() => {
    if (showTotals && players && players.length > 0) {
      const { completedRounds, currentRound } = calculateRoundCompletion();
      
      // Calculate all totals in one pass
      let totalSkinsCompleted = 0;
      let totalSawoffsCompleted = 0;
      let totalSkinsCompletedAndCurrent = 0;
      let totalSawoffsCompletedAndCurrent = 0;

      players.forEach(player => {
        // Count skins for completed rounds
        if (player.skinInfo && Array.isArray(player.skinInfo)) {
          const skinsCompleted = player.skinInfo.filter(skin => skin.round <= completedRounds).length;
          const skinsCompletedAndCurrent = player.skinInfo.filter(skin => skin.round <= currentRound).length;
          
          totalSkinsCompleted += skinsCompleted;
          totalSkinsCompletedAndCurrent += skinsCompletedAndCurrent;
        }

        // Count saw-offs for completed rounds
        if (player.sawOffInfo && Array.isArray(player.sawOffInfo)) {
          const sawOffsCompleted = player.sawOffInfo.filter(sawOff => sawOff.round <= completedRounds).length;
          const sawOffsCompletedAndCurrent = player.sawOffInfo.filter(sawOff => sawOff.round <= currentRound).length;
          
          totalSawoffsCompleted += sawOffsCompleted;
          totalSawoffsCompletedAndCurrent += sawOffsCompletedAndCurrent;
        }
      });
      
      if (onTotalsUpdate) {
        onTotalsUpdate([
          totalSkinsCompleted,           // [0] Total Skins (completed rounds)
          Math.floor(totalSawoffsCompleted / 2),  // [1] Total Sawoffs (completed rounds)
          totalSkinsCompletedAndCurrent, // [2] Total Skins* (completed + current)
          Math.floor(totalSawoffsCompletedAndCurrent / 2) // [3] Total Sawoffs* (completed + current)
        ]);
      }
    }
  }, [players, onTotalsUpdate, showTotals, tournament]);

  if (!players || players.length === 0) {
    return (
      <div className="all-players-table-container">
        <h3 className="all-players-title">{title}</h3>
        <div className="empty-state no-players">
          <p>No players data available</p>
        </div>
      </div>
    );
  }

  return (
    <div className="all-players-table-container">
      <h3 className="all-players-title">{title}</h3>
      <div className="all-players-table-wrapper">
        <table className="all-players-table">
          <thead>
            <tr>
              <th>Position</th>
              <th>Name (Tier)</th>
              <th>Username</th>
              <th>Total Score</th>
              <th>Skins</th>
              <th>Saw-off</th>
              <th>Skin Details</th>
              <th>Saw-off Details</th>
              {showRemoveButton && <th>Action</th>}
            </tr>
          </thead>
          <tbody>
            {players.map((player, index) => (
              <tr key={player.golferId || player.id || index} className="player-row">
                <td className="position-cell">
                  <span className="position-number">
                    {player.position || (index + 1)}
                  </span>
                </td>
                <td className="name-tier-cell">
                  <div className="player-info">
                    <span className="player-name">{player.golferName || player.name}</span>
                    <div className="player-details">
                      <span className={`tier-badge ${getTierColor(player.golferTier || player.tier)}`}>
                        {formatTierText(player.golferTier || player.tier)}
                      </span>
                      {player.worldRanking && (
                        <span className="world-ranking">#{player.worldRanking}</span>
                      )}
                    </div>
                  </div>
                </td>
                <td className="username-cell">
                  <span className="username">
                    {formatUsername(player.userName || player.username || player.golferUsername || 'N/A')}
                  </span>
                </td>
                <td className="score-cell">
                  <span className="total-score">
                    {player.totalScore || player.score || 0}
                  </span>
                </td>
                <td className="skin-cell">
                  <div className="skin-info">
                    <span className="skin-count">{player.totalSkins || player.skins || 0}</span>
                    <div className="skin-prize-info">
                      <span className="skin-prize">
                        ${calculateSkinPrize(player.totalSkins || player.skins || 0, totalSkins).toFixed(0)}
                      </span>
                      <span className="skin-percentage">
                        ({player.totalSkinsPercentage ? `${player.totalSkinsPercentage}%` : calculatePercentage(player.totalSkins || player.skins || 0, totalSkins)})
                      </span>
                    </div>
                  </div>
                </td>
                <td className="sawoff-cell">
                  <div className="sawoff-info">
                    <span className="sawoff-prize">
                      ${calculateSawOffPrize(player.sawOffInfo).toFixed(0)}
                    </span>
                    <span className="sawoff-count">{player.totalSawOffs || player.sawoffs || 0}</span>
                  </div>
                </td>
                <td className="skin-details-cell">
                  <div className="skin-details-container">
                    {formatSkinInfo(player.skinInfo).map((item, idx) => {
                      // Determine CSS class based on round status and skin type
                      const cssClass = item.isCompleted 
                        ? `skin-detail-badge skin-detail-${item.type.toLowerCase()}` 
                        : 'skin-detail-badge skin-detail-current';
                      
                      return (
                        <span key={idx} className={cssClass}>
                          {item.text}
                        </span>
                      );
                    })}
                  </div>
                </td>
                <td className="sawoff-details-cell">
                  <div className="sawoff-details-container">
                    {formatSawOffInfo(player.sawOffInfo).map((item, idx) => {
                      // Determine CSS class based on round status and saw-off type
                      const cssClass = item.isCompleted 
                        ? `sawoff-detail-badge sawoff-detail-${item.type.toLowerCase()}` 
                        : 'sawoff-detail-badge sawoff-detail-current';
                      
                      return (
                        <span key={idx} className={cssClass}>
                          {item.text}
                        </span>
                      );
                    })}
                  </div>
                </td>
                {showRemoveButton && (
                  <td className="action-cell">
                    <button
                      onClick={() => handleRemovePlayer(player)}
                      disabled={removingPlayer === player.golferId || tournament?.status === 'LIVE'}
                      className={`remove-player-btn ${removingPlayer === player.golferId ? 'removing' : ''} ${tournament?.status === 'LIVE' ? 'disabled' : ''}`}
                      title={tournament?.status === 'LIVE' ? 'Cannot remove players from live tournament' : 'Remove player and get refund'}
                    >
                      {removingPlayer === player.golferId ? 'REMOVING...' : 'REMOVE'}
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AllPlayersTable;
